import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { auth, db } from '../firebase/firebase';
import { onAuthStateChanged, onIdTokenChanged, multiFactor, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { notify } from '../utils/notifications';
import InactivityWarningModal from '../components/InactivityWarningModal';
import AiAcknowledgmentModal from '../components/AiAcknowledgmentModal';

const AuthContext = createContext({});

// C2: 30 minutes (was 15). Firebase asks for the second factor only when a
// session is created, so idle logout is the main source of repeat challenges
// for admins.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;      // 30 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000;      // Show warning 2 minutes before
const WARNING_AT_MS = IDLE_TIMEOUT_MS - WARNING_BEFORE_MS; // 28 minutes

const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart'];

/**
 * C2: null when there is no user or the SDK cannot read the user (test doubles
 * lack multiFactor support), else whether at least one second factor is enrolled.
 */
function readMfaEnrolled(u) {
    if (!u) return null;
    try {
        return multiFactor(u).enrolledFactors.length > 0;
    } catch (_) {
        return null;
    }
}

/**
 * Convert a raw Firestore user document into the shape the rest of the app
 * expects (snake_case keys, ISO strings for timestamps).
 */
export function toProfileResponse(data) {
    if (!data) return null;
    const toISO = (v) => (v && typeof v.toDate === 'function') ? v.toDate().toISOString() : v ?? null;
    return {
        name: data.name || '',
        organization: data.organization || '',
        tos_accepted_at: toISO(data.tosAcceptedAt),
        onboarding_dismissed_at: toISO(data.onboardingDismissedAt),
        fact_review_onboarding_dismissed_at: toISO(data.factReviewOnboardingDismissedAt),
        email_verified: data.emailVerified ?? false,
        facility_type: data.facilityType || '',
        facility_type_other: data.facilityTypeOther ?? null,
        facility_id: data.facilityId ?? null,
        facility_role: data.facilityRole ?? null,
        created_at: toISO(data.createdAt),
    };
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [profileReady, setProfileReady] = useState(false);
    // C1: null = not yet known, false = unverified, true = verified.
    const [emailVerified, setEmailVerified] = useState(null);
    // C2: null = not yet known, false = no second factor, true = enrolled.
    const [mfaEnrolled, setMfaEnrolled] = useState(null);
    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const [aiAcknowledged, setAiAcknowledged] = useState(
        () => sessionStorage.getItem('aiAcknowledged') === 'true'
    );
    const navigate = useNavigate();

    const idleTimerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const signOutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const profileUnsubRef = useRef(null);

    const checkAdmin = useCallback(async () => {
        try {
            await api.get('/admin/users');
            setIsAdmin(true);
        } catch {
            setIsAdmin(false);
        }
    }, []);

    // --- Idle timeout logic ---

    const clearIdleTimers = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    }, []);

    const resetIdleTimer = useCallback(() => {
        clearIdleTimers();
        setShowIdleWarning(false);
        lastActivityRef.current = Date.now();

        // Show warning at 28 minutes, then start logout countdown
        warningTimerRef.current = setTimeout(() => {
            setShowIdleWarning(true);

            // Auto-logout 2 minutes after warning
            idleTimerRef.current = setTimeout(() => {
                setShowIdleWarning(false);
                if (signOutRef.current) {
                    signOutRef.current(true);
                }
            }, WARNING_BEFORE_MS);
        }, WARNING_AT_MS);
    }, [clearIdleTimers]);

    const handleStayLoggedIn = useCallback(() => {
        resetIdleTimer();
    }, [resetIdleTimer]);

    // Attach/detach activity listeners when user logs in/out
    useEffect(() => {
        if (!user) {
            clearIdleTimers();
            return;
        }

        const onActivity = () => {
            // If idle timeout has already elapsed (e.g. browser was suspended
            // during laptop sleep), force logout immediately
            const elapsed = Date.now() - lastActivityRef.current;
            if (elapsed >= IDLE_TIMEOUT_MS) {
                if (signOutRef.current) {
                    signOutRef.current(true);
                }
                return;
            }

            setShowIdleWarning(false);
            resetIdleTimer();
        };

        ACTIVITY_EVENTS.forEach(event =>
            document.addEventListener(event, onActivity, { passive: true })
        );

        resetIdleTimer();

        return () => {
            ACTIVITY_EVENTS.forEach(event =>
                document.removeEventListener(event, onActivity)
            );
            clearIdleTimers();
        };
    }, [user, resetIdleTimer, clearIdleTimers]);

    // --- Auth state ---

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // Tear down previous profile listener
            if (profileUnsubRef.current) {
                profileUnsubRef.current();
                profileUnsubRef.current = null;
            }

            if (firebaseUser) {
                setUser(firebaseUser);
                // Mark profile as not ready synchronously so any already-rendered
                // consumers (e.g. HomeIndex after a login transition where loading
                // is already false) wait for the first Firestore snapshot before
                // making role-based decisions.
                setProfileReady(false);

                // Listen to user profile in Firestore — any change (admin or
                // self) is reflected immediately without manual refresh.
                // We gate setLoading(false) on the FIRST snapshot so that
                // isFacilityAdmin/facilityId are already populated before
                // children render (prevents a race where checkAdmin() wins and
                // HomeIndex redirects to /cases for facility admins).
                const userDocRef = doc(db, 'users', firebaseUser.uid);
                const firstSnapshotPromise = new Promise((resolve) => {
                    let resolved = false;
                    profileUnsubRef.current = onSnapshot(
                        userDocRef,
                        (snap) => {
                            if (snap.exists()) {
                                setUserProfile(toProfileResponse(snap.data()));
                            } else {
                                setUserProfile(null);
                            }
                            if (!resolved) {
                                resolved = true;
                                resolve();
                            }
                        },
                        (error) => {
                            console.error('Error loading user profile:', error);
                            setUserProfile(null);
                            if (!resolved) {
                                resolved = true;
                                resolve();
                            }
                        }
                    );
                });

                await Promise.all([checkAdmin(), firstSnapshotPromise]);
                setProfileReady(true);
            } else {
                setUser(null);
                setUserProfile(null);
                setIsAdmin(false);
                // No profile to wait for when logged out.
                setProfileReady(true);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            if (profileUnsubRef.current) {
                profileUnsubRef.current();
                profileUnsubRef.current = null;
            }
        };
    }, [checkAdmin]);

    // C1: track emailVerified through onIdTokenChanged, not onAuthStateChanged.
    // onAuthStateChanged does not refire on getIdToken(true), so the forced
    // refresh in VerifyEmail's "I have verified" would leave React state stale
    // and ProtectedRoute would depend on Firebase mutating the shared user
    // object. onIdTokenChanged fires on that refresh and carries the new flag.
    useEffect(() => {
        const unsubscribe = onIdTokenChanged(auth, (u) => {
            setEmailVerified(u ? u.emailVerified === true : null);
            setMfaEnrolled(readMfaEnrolled(u));
        });
        return unsubscribe;
    }, []);

    // C2: MfaSetup calls this after enroll(); enrolledFactors is refreshed by
    // reload(), which does not fire onIdTokenChanged on its own.
    const refreshMfaState = useCallback(async () => {
        const u = auth.currentUser;
        if (u) {
            try {
                await u.reload();
            } catch (_) {
                // Offline or stale session: fall through and read what we have.
            }
        }
        setMfaEnrolled(readMfaEnrolled(u));
    }, []);

    const signOut = useCallback(async (fromInactivity = false) => {
        try {
            clearIdleTimers();
            if (profileUnsubRef.current) {
                profileUnsubRef.current();
                profileUnsubRef.current = null;
            }
            await firebaseSignOut(auth);
            setUser(null);
            setUserProfile(null);
            setIsAdmin(false);
            setAiAcknowledged(false);
            sessionStorage.removeItem('aiAcknowledged');
            navigate('/login');
            if (fromInactivity) {
                notify.info('You were logged out due to inactivity.');
            }
        } catch (error) {
            console.error("Error signing out: ", error);
        }
    }, [navigate, clearIdleTimers]);

    // Keep signOutRef in sync so the timeout callback always uses the latest
    useEffect(() => {
        signOutRef.current = signOut;
    }, [signOut]);

    const acknowledgeAi = useCallback(() => {
        setAiAcknowledged(true);
        sessionStorage.setItem('aiAcknowledged', 'true');
    }, []);

    const facilityId = userProfile?.facility_id ?? null;
    const facilityRole = userProfile?.facility_role ?? null;
    const isFacilityAdmin = facilityRole === 'facility_admin';

    const value = {
        user,
        userProfile,
        emailVerified,
        mfaEnrolled,
        refreshMfaState,
        isAdmin,
        facilityId,
        facilityRole,
        isFacilityAdmin,
        aiAcknowledged,
        signOut,
        loading,
        profileReady,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
            {user && !aiAcknowledged && (
                <AiAcknowledgmentModal onAcknowledge={acknowledgeAi} />
            )}
            {showIdleWarning && (
                <InactivityWarningModal
                    secondsRemaining={Math.floor(WARNING_BEFORE_MS / 1000)}
                    onStayLoggedIn={handleStayLoggedIn}
                />
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
